import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check, X, Trash2, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const ICONOS = {
  asignacion: '🔧', pieza: '📦', urgente: '🔴', recogida: '🔔', manual: '💬',
};
const COLOR_PRIORIDAD = {
  1: 'border-l-red-500', 2: 'border-l-orange-400',
  3: 'border-l-blue-400', 4: 'border-l-gray-300',
};

export default function AvisosCampana({ darkMode }) {
  const [abierto, setAbierto] = useState(false);
  const [avisos, setAvisos] = useState([]);
  const [contador, setContador] = useState({ total: 0, urgentes: 0 });
  const [ajustes, setAjustes] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const ref = useRef(null);
  const anterior = useRef(0);
  const navigate = useNavigate();

  // Se consulta cada 20 s: suficiente para avisos y sin cargar el servidor
  const cargarContador = useCallback(async () => {
    try {
      const { data } = await api.get('/avisos/contador');
      setContador({ total: data.total, urgentes: data.urgentes });

      // Aviso sonoro solo cuando llega algo nuevo y lo tiene activado
      if (data.total > anterior.current && anterior.current !== 0) {
        try {
          const p = await api.get('/avisos/preferencias');
          if (p.data.data?.sonido) pitido();
        } catch (e) {}
      }
      anterior.current = data.total;
    } catch (e) {}
  }, []);

  useEffect(() => {
    cargarContador();
    const t = setInterval(cargarContador, 20000);
    return () => clearInterval(t);
  }, [cargarContador]);

  const cargarAvisos = useCallback(async () => {
    try {
      const { data } = await api.get('/avisos');
      setAvisos(data.data || []);
      setContador(c => ({ ...c, total: data.nuevos }));
    } catch (e) {}
  }, []);

  useEffect(() => { if (abierto) cargarAvisos(); }, [abierto, cargarAvisos]);

  useEffect(() => {
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) { setAbierto(false); setAjustes(false); } };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  // Pitido corto generado en el navegador, sin archivos de sonido
  const pitido = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gan = ctx.createGain();
      osc.connect(gan); gan.connect(ctx.destination);
      osc.frequency.value = 880;
      gan.gain.setValueAtTime(0.12, ctx.currentTime);
      gan.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(); osc.stop(ctx.currentTime + 0.3);
    } catch (e) {}
  };

  const abrirAviso = async (a) => {
    if (!a.leido) {
      try { await api.patch(`/avisos/${a.id}/leido`); } catch (e) {}
    }
    setAbierto(false);
    cargarContador(); cargarAvisos();
    if (a.referencia_tipo === 'reparacion' && a.referencia_id) {
      navigate(`/reparaciones?abrir=${a.referencia_id}`);
    } else if (a.enlace) {
      navigate(a.enlace);
    }
  };

  const leerTodos = async () => {
    try {
      await api.patch('/avisos/leer-todos');
      cargarAvisos(); cargarContador();
    } catch (e) { toast.error('No se pudo marcar como leído'); }
  };

  const borrar = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/avisos/${id}`);
      setAvisos(prev => prev.filter(a => a.id !== id));
      cargarContador();
    } catch (e) {}
  };

  const abrirAjustes = async () => {
    try {
      const { data } = await api.get('/avisos/preferencias');
      setPrefs(data.data);
      setAjustes(true);
    } catch (e) { toast.error('No se pudieron cargar las preferencias'); }
  };

  const guardarPrefs = async (nuevas) => {
    setPrefs(nuevas);
    try { await api.put('/avisos/preferencias', nuevas); }
    catch (e) { toast.error('No se pudo guardar'); }
  };

  const hace = (iso) => {
    try {
      const d = new Date(String(iso).replace(' ', 'T'));
      const min = Math.floor((Date.now() - d.getTime()) / 60000);
      if (min < 1) return 'ahora';
      if (min < 60) return `hace ${min} min`;
      const h = Math.floor(min / 60);
      if (h < 24) return `hace ${h} h`;
      return d.toLocaleDateString('es-ES');
    } catch (e) { return ''; }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setAbierto(v => !v); setAjustes(false); }}
        className="relative p-2 rounded-xl transition-colors"
        style={{ color: darkMode ? '#94a3b8' : '#6b7280' }}
        title="Mis avisos">
        <Bell size={19}/>
        {contador.total > 0 && (
          <span className={clsx('absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 rounded-full',
            'text-[10px] font-bold text-white flex items-center justify-center',
            contador.urgentes > 0 ? 'bg-red-500' : 'bg-blue-500')}>
            {contador.total > 99 ? '99+' : contador.total}
          </span>
        )}
      </button>

      {abierto && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 rounded-2xl shadow-xl z-50 overflow-hidden"
          style={{
            background: darkMode ? '#1a1a2e' : 'white',
            border: darkMode ? '1px solid rgba(255,255,255,.1)' : '1px solid rgba(0,0,0,.08)',
          }}>

          <div className="flex items-center justify-between px-4 py-2.5"
            style={{ borderBottom: darkMode ? '1px solid rgba(255,255,255,.06)' : '1px solid #f3f4f6' }}>
            <span className="text-sm font-semibold" style={{ color: darkMode ? '#e2e8f0' : '#111827' }}>
              Mis avisos {contador.total > 0 && <span className="text-blue-500">({contador.total})</span>}
            </span>
            <div className="flex items-center gap-1">
              {contador.total > 0 && (
                <button onClick={leerTodos} className="text-xs text-blue-600 hover:underline px-1">
                  Marcar leídos
                </button>
              )}
              <button onClick={abrirAjustes} className="p-1 text-gray-400 hover:text-gray-700" title="Qué avisos recibo">
                <Settings size={15}/>
              </button>
            </div>
          </div>

          {ajustes && prefs ? (
            <div className="p-4 space-y-2.5">
              <p className="text-xs font-semibold text-gray-500 uppercase">Quiero que me avisen de</p>
              {[
                ['asignacion', '🔧 Reparación asignada a mí'],
                ['pieza', '📦 Pieza recibida para mi reparación'],
                ['urgente', '🔴 Reparación urgente'],
                ['recogida', '🔔 Cliente espera recogida'],
                ['sonido', '🔊 Sonido al llegar un aviso'],
              ].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer"
                  style={{ color: darkMode ? '#cbd5e1' : '#374151' }}>
                  <input type="checkbox" className="rounded border-gray-300"
                    checked={!!prefs[k]}
                    onChange={e => guardarPrefs({ ...prefs, [k]: e.target.checked ? 1 : 0 })}/>
                  {l}
                </label>
              ))}
              <button onClick={() => setAjustes(false)} className="btn-secondary text-xs w-full mt-2">
                Volver a los avisos
              </button>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              {avisos.length === 0 ? (
                <p className="text-center text-sm text-gray-400 py-10">No tienes avisos</p>
              ) : avisos.map(a => (
                <button key={a.id} onClick={() => abrirAviso(a)}
                  className={clsx('w-full text-left px-4 py-3 border-l-4 transition-colors flex gap-2.5',
                    COLOR_PRIORIDAD[a.prioridad] || 'border-l-gray-300',
                    !a.leido && (darkMode ? 'bg-white/5' : 'bg-blue-50/40'))}
                  style={{ borderBottom: darkMode ? '1px solid rgba(255,255,255,.04)' : '1px solid #f9fafb' }}>
                  <span className="text-lg flex-shrink-0">{ICONOS[a.tipo] || '💬'}</span>
                  <div className="flex-1 min-w-0">
                    <p className={clsx('text-sm truncate', !a.leido && 'font-semibold')}
                       style={{ color: darkMode ? '#e2e8f0' : '#111827' }}>
                      {a.titulo}
                    </p>
                    {a.mensaje && (
                      <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{a.mensaje}</p>
                    )}
                    <p className="text-[11px] text-gray-400 mt-1">
                      {hace(a.created_at)}
                      {a.creado_por_nombre ? ` · ${a.creado_por_nombre}` : ''}
                    </p>
                  </div>
                  <span onClick={(e) => borrar(e, a.id)}
                        className="p-1 text-gray-300 hover:text-red-500 flex-shrink-0 cursor-pointer">
                    <Trash2 size={13}/>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
